// Freight management — sub-tab inside Transport.
// Three views in one component: Master (rate card CRUD) · Lines (per-dispatch
// freight rows) · Bills (group lines into invoiced freight bills).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { freightAPI, dealerAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Download, FileText, Receipt, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';
import { useWindowedList } from '../../hooks/useWindowedList';

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { const d = new Date(iso); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return iso; }
};

export const FreightSection = ({ transporters = [], userRole }) => {
  const isAdmin = userRole === 'admin';
  // Non-admins should never see or land on Master / Dealer Splits — those are
  // rate-card & split configuration screens (admin-only). Default landing view
  // becomes 'lines' either way; non-admins get a trimmed sub-nav.
  const [view, setView] = useState('lines'); // master | lines | bills | overrides
  // Safety net: if the persisted view is admin-only and the role changes
  // (e.g. impersonation), snap back to 'lines'.
  React.useEffect(() => {
    if (!isAdmin && (view === 'master' || view === 'overrides')) setView('lines');
  }, [isAdmin, view]);

  const subViews = isAdmin
    ? [
        { id: 'master', label: 'Master' },
        { id: 'overrides', label: 'Dealer Splits' },
        { id: 'lines', label: 'Lines' },
        { id: 'bills', label: 'Bills' },
      ]
    : [
        { id: 'lines', label: 'Lines' },
        { id: 'bills', label: 'Bills' },
      ];

  return (
    <div data-testid="freight-section">
      <div className="inline-flex rounded border overflow-hidden mb-4 flex-wrap" style={{ borderColor: '#2B5339' }} data-testid="freight-views">
        {subViews.map((v, i) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`px-4 py-1.5 text-sm font-medium ${i > 0 ? 'border-l' : ''}`}
            style={{
              background: view === v.id ? '#2B5339' : 'white',
              color: view === v.id ? 'white' : '#2B5339',
              borderColor: '#2B5339',
            }}
            data-testid={`freight-view-${v.id}`}
          >{v.label}</button>
        ))}
      </div>
      {isAdmin && view === 'master' && <MasterView />}
      {isAdmin && view === 'overrides' && <DealerOverridesView />}
      {view === 'lines' && <LinesView transporters={transporters} />}
      {view === 'bills' && <BillsView transporters={transporters} />}
    </div>
  );
};

// ============================ MASTER VIEW ============================
const MasterView = () => {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ id: null, location: '', base_weight_kg: 4000, base_rate: 0, by_dealer_at_base: 0, default_toll: 0, default_misc: 0, notes: '' });
  // Search — case-insensitive substring on location + notes.
  const [search, setSearch] = useState('');
  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.location || '').toLowerCase().includes(q) ||
      (r.notes || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, locs] = await Promise.all([freightAPI.listMaster(), freightAPI.listLocations()]);
      setRows(m.data);
      setLocations(locs.data);
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load master'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ id: null, location: '', base_weight_kg: 4000, base_rate: 0, by_dealer_at_base: 0, default_toll: 0, default_misc: 0, notes: '' }); setDialogOpen(true); };
  const openEdit = (r) => {
    setForm({
      id: r.id,
      location: r.location || '',
      base_weight_kg: r.base_weight_kg || 4000,
      base_rate: r.base_rate || 0,
      by_dealer_at_base: r.by_dealer_at_base || 0,
      default_toll: r.default_toll || 0,
      default_misc: r.default_misc || 0,
      notes: r.notes || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.location.trim()) { toast.error('Location is required'); return; }
    const body = {
      location: form.location.trim(),
      base_weight_kg: parseInt(form.base_weight_kg) || 4000,
      base_rate: parseFloat(form.base_rate) || 0,
      by_dealer_at_base: parseFloat(form.by_dealer_at_base) || 0,
      default_toll: parseFloat(form.default_toll) || 0,
      default_misc: parseFloat(form.default_misc) || 0,
      notes: form.notes || '',
    };
    try {
      if (form.id) {
        await freightAPI.updateMaster(form.id, body);
        toast.success('Rate updated');
      } else {
        await freightAPI.createMaster(body);
        toast.success('Rate added');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeRow = async (r) => {
    if (!(await confirm({
      title: `Delete rate for ${r.location}?`,
      description: 'Un-billed freight lines that auto-resolved against this row will fall back to manual.',
      confirmLabel: 'Delete',
      destructive: true,
    }))) return;
    try { await freightAPI.deleteMaster(r.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Suggest locations that don't already have a master row.
  const existingLocs = useMemo(() => new Set(rows.map(r => (r.location || '').toLowerCase())), [rows]);
  const availableLocations = useMemo(() => {
    if (form.id) return locations; // editing → all towns selectable
    return locations.filter(l => !existingLocs.has(l.toLowerCase()));
  }, [locations, existingLocs, form.id]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm" style={{ color: '#5C6B61' }}>
          Rate per location, scaled by weight. Base weight default 4000 kg — dealer share rounds to nearest 50 (down when weight ↑, up when weight ↓).
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}/api/freight/master/template`,
                  { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
                );
                const a = document.createElement('a');
                a.href = URL.createObjectURL(new Blob([res.data]));
                a.download = 'freight_master_template.xlsx';
                document.body.appendChild(a); a.click(); a.remove();
              } catch (e) { toast.error('Download failed'); }
            }}
            size="sm"
            variant="outline"
            style={{ borderColor: '#2B5339', color: '#2B5339' }}
            data-testid="freight-master-template"
            title="Download Excel template with all dealer towns pre-filled"
          ><FileSpreadsheet className="w-4 h-4 mr-1" /> Download template</Button>
          <label className="cursor-pointer inline-flex items-center gap-1 h-9 px-3 rounded border text-sm font-medium hover:bg-[#FBF9F6]" style={{ borderColor: '#2B5339', color: '#2B5339' }} data-testid="freight-master-upload-label" title="Upload the filled template to bulk-update the master">
            <Download className="w-4 h-4 rotate-180" /> Upload Excel
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              data-testid="freight-master-upload-input"
              onChange={async (e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                try {
                  const res = await freightAPI.uploadMaster(f);
                  const { inserted, updated, skipped } = res.data;
                  toast.success(`Master updated: ${inserted} added · ${updated} edited · ${skipped} blank rows skipped`);
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Upload failed');
                } finally {
                  e.target.value = ''; // reset for re-upload
                }
              }}
            />
          </label>
          <Button onClick={openAdd} size="sm" style={{ background: '#2B5339' }} data-testid="freight-master-add">
            <Plus className="w-4 h-4 mr-1" /> Add rate
          </Button>
        </div>
      </div>
      {/* Search bar — filters master rows by location or notes */}
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search location or notes…"
            className="h-9 pr-8"
            data-testid="freight-master-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute top-1/2 right-2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ color: '#C05C35', background: '#FBF9F6', border: '1px solid #E2DFD8' }}
              data-testid="freight-master-search-clear"
              aria-label="Clear"
            >×</button>
          )}
        </div>
        <span className="text-xs" style={{ color: '#5C6B61' }}>{filteredRows.length} of {rows.length} rate{rows.length === 1 ? '' : 's'}</span>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: '#FBF9F6' }}>
            <tr>
              {['Location', 'Base Wt (kg)', 'Base Rate', 'By Dealer @ Base', 'Toll', 'Misc', 'Notes', ''].map((h) => (
                <th key={h} className="p-2 text-left font-medium whitespace-nowrap" style={{ color: '#5C6B61' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-6 text-center" style={{ color: '#5C6B61' }}>Loading…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center" style={{ color: '#5C6B61' }}>{rows.length === 0 ? 'No rates configured yet. Add one to start auto-filling freight.' : `No rates match "${search}".`}</td></tr>
            ) : filteredRows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="freight-master-row">
                <td className="p-2 font-medium">{r.location}</td>
                <td className="p-2 tabular-nums">{r.base_weight_kg || 4000}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(r.base_rate)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(r.by_dealer_at_base)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(r.default_toll)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(r.default_misc)}</td>
                <td className="p-2 text-xs" style={{ color: '#5C6B61' }}>{r.notes || '-'}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="h-7 mr-1" data-testid="freight-master-edit"><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => removeRow(r)} className="h-7" style={{ borderColor: '#C05C35', color: '#C05C35' }} data-testid="freight-master-delete"><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? 'Edit Rate' : 'Add Rate'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium">Location *</label>
              <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full h-9 px-2 border rounded text-sm bg-white" data-testid="freight-master-location">
                <option value="">-- pick a dealer town --</option>
                {availableLocations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>
                Pulled from dealer master ({locations.length} town{locations.length === 1 ? '' : 's'} total). Auto-syncs as new dealers are added.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium">Base Weight (kg)</label>
              <Input type="number" value={form.base_weight_kg} onChange={(e) => setForm({ ...form, base_weight_kg: e.target.value })} data-testid="freight-master-base-weight" />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Default 4000 kg.</p>
            </div>
            <div>
              <label className="text-xs font-medium">Base Rate (₹)</label>
              <Input type="number" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} data-testid="freight-master-rate" />
            </div>
            <div>
              <label className="text-xs font-medium">By Dealer @ Base (₹)</label>
              <Input type="number" value={form.by_dealer_at_base} onChange={(e) => setForm({ ...form, by_dealer_at_base: e.target.value })} data-testid="freight-master-by-dealer" />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Us = Base Rate − this. Both scale with weight.</p>
            </div>
            <div>
              <label className="text-xs font-medium">By Us @ Base (₹)</label>
              <Input type="number" value={Math.max(0, (parseFloat(form.base_rate) || 0) - (parseFloat(form.by_dealer_at_base) || 0))} readOnly className="bg-[#FBF9F6]" />
            </div>
            <div>
              <label className="text-xs font-medium">Default Toll (₹)</label>
              <Input type="number" value={form.default_toll} onChange={(e) => setForm({ ...form, default_toll: e.target.value })} data-testid="freight-master-toll" />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Flat, borne by dealer.</p>
            </div>
            <div>
              <label className="text-xs font-medium">Default Misc (₹)</label>
              <Input type="number" value={form.default_misc} onChange={(e) => setForm({ ...form, default_misc: e.target.value })} data-testid="freight-master-misc" />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Flat, borne by dealer.</p>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} style={{ background: '#2B5339' }} data-testid="freight-master-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================ DEALER OVERRIDES VIEW ============================
const DealerOverridesView = () => {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [locations, setLocations] = useState([]);
  // Master rate rows — needed to display the base_rate / by_dealer / by_us
  // reference values in the override form once the operator picks a dealer.
  const [masterRows, setMasterRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // `id` is set when editing an existing override (null for new).
  const [form, setForm] = useState({ id: null, dealer_id: '', location: '', by_dealer_at_base: 0, toll: '', misc: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [o, d, l, m] = await Promise.all([
        freightAPI.listDealerOverrides(),
        dealerAPI.getAll(),
        freightAPI.listLocations(),
        freightAPI.listMaster(),
      ]);
      setRows(o.data);
      setDealers(d.data || []);
      setLocations(l.data);
      setMasterRows(m.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load overrides'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Selected dealer + their default location (from dealer.town).
  const selectedDealer = React.useMemo(
    () => dealers.find(d => d.id === form.dealer_id) || null,
    [dealers, form.dealer_id]
  );
  const defaultLocation = (selectedDealer?.town || '').trim();
  // Master row for this dealer's default location (case-insensitive).
  const masterForDealer = React.useMemo(() => {
    if (!defaultLocation) return null;
    const target = defaultLocation.toLowerCase();
    return masterRows.find(m => (m.location || '').trim().toLowerCase() === target) || null;
  }, [masterRows, defaultLocation]);
  // Whenever the operator changes the dealer, sync the form.location so the
  // save() payload picks it up automatically.
  React.useEffect(() => {
    if (form.dealer_id && defaultLocation && form.location !== defaultLocation) {
      setForm(f => ({ ...f, location: defaultLocation }));
    }
  }, [form.dealer_id, defaultLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setForm({ id: null, dealer_id: '', location: '', by_dealer_at_base: 0, toll: '', misc: '' });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      id: r.id,
      dealer_id: r.dealer_id,
      location: r.location || '',
      by_dealer_at_base: r.by_dealer_at_base || 0,
      toll: (r.toll === null || r.toll === undefined) ? '' : String(r.toll),
      misc: (r.misc === null || r.misc === undefined) ? '' : String(r.misc),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.dealer_id || !form.location) { toast.error('Pick a dealer with a valid town'); return; }
    // Empty string in Toll/Misc → null (fall back to master default).
    // Any entered value (incl. 0) → override the master default.
    const parseOpt = (v) => (v === '' || v === null || v === undefined) ? null : (parseFloat(v) || 0);
    const payload = {
      dealer_id: form.dealer_id,
      location: form.location,
      by_dealer_at_base: parseFloat(form.by_dealer_at_base) || 0,
      toll: parseOpt(form.toll),
      misc: parseOpt(form.misc),
    };
    // Optimistic UI: close the dialog and patch the list IMMEDIATELY so the
    // operator sees their change without waiting for the round-trip + refetch.
    // (Fix 18/Feb/2026 for the "Save doesn't close popup" + "delete lingers"
    // pair of bugs on Freight → Dealer Splits.)
    const dealerName = selectedDealer?.name || '';
    const optimisticId = form.id || `pending-${Date.now()}`;
    setRows(prev => {
      const idx = prev.findIndex(r => r.dealer_id === payload.dealer_id && (r.location || '').toLowerCase() === (payload.location || '').toLowerCase());
      const merged = { id: optimisticId, ...payload, dealer_name: dealerName };
      if (idx === -1) return [...prev, merged];
      const next = [...prev];
      next[idx] = { ...prev[idx], ...payload };
      return next;
    });
    setDialogOpen(false);
    try {
      await freightAPI.upsertDealerOverride(payload);
      toast.success('Override saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      load();
    }
  };

  const removeRow = async (r) => {
    if (!(await confirm({
      title: `Remove override for ${r.dealer_name} at ${r.location}?`,
      description: 'This dealer will revert to the location master\'s default split.',
      confirmLabel: 'Remove',
      destructive: true,
    }))) return;
    // Optimistic: remove from the list immediately; only refetch on error.
    setRows(prev => prev.filter(x => x.id !== r.id));
    try {
      await freightAPI.deleteDealerOverride(r.id);
      toast.success('Removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
      load();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm" style={{ color: '#5C6B61' }}>
          Per-dealer split overrides — replaces the location's default dealer share for that dealer only. Persists until edited or removed.
        </p>
        <Button onClick={openAdd} size="sm" style={{ background: '#2B5339' }} data-testid="freight-override-add">
          <Plus className="w-4 h-4 mr-1" /> Add override
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: '#FBF9F6' }}>
            <tr>
              {['Dealer', 'Location', 'By Dealer @ Base (₹)', ''].map((h) => (
                <th key={h} className="p-2 text-left font-medium" style={{ color: '#5C6B61' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="p-6 text-center" style={{ color: '#5C6B61' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="p-6 text-center" style={{ color: '#5C6B61' }}>No dealer overrides yet. Master defaults will apply to every dealer.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="freight-override-row">
                <td className="p-2 font-medium">{r.dealer_name || '(unknown)'}</td>
                <td className="p-2">{r.location}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(r.by_dealer_at_base)}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="h-7 mr-1" data-testid="freight-override-edit"><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => removeRow(r)} className="h-7" style={{ borderColor: '#C05C35', color: '#C05C35' }} data-testid="freight-override-delete"><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? 'Edit Dealer Split Override' : 'Add Dealer Split Override'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Dealer *</label>
              <select value={form.dealer_id} onChange={(e) => setForm({ ...form, dealer_id: e.target.value })} className="w-full h-9 px-2 border rounded text-sm bg-white" data-testid="freight-override-dealer">
                <option value="">-- pick dealer --</option>
                {[...dealers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(d => <option key={d.id} value={d.id}>{d.name}{d.town ? ` · ${d.town}` : ''}</option>)}
              </select>
              {form.dealer_id && !defaultLocation && (
                <p className="text-[10px] mt-1" style={{ color: '#C05C35' }}>
                  This dealer has no town set — override cannot be saved. Update the dealer's town on the Dealers tab first.
                </p>
              )}
            </div>
            {/* Read-only master reference — appears once a dealer is selected.
                Shows the current base rate + split for the dealer's town so the
                operator can decide the override amount in context. */}
            {form.dealer_id && defaultLocation && (
              <div className="rounded border p-3" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }} data-testid="freight-override-master-ref">
                <div className="text-xs font-semibold mb-2" style={{ color: '#2B5339' }}>
                  Default Location: <span className="font-bold">{defaultLocation}</span>
                </div>
                {masterForDealer ? (
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <div style={{ color: '#5C6B61' }}>Base Weight</div>
                      <div className="font-bold tabular-nums" style={{ color: '#1F1F1F' }} data-testid="freight-override-master-base-weight">{Math.round(masterForDealer.base_weight_kg || 0).toLocaleString('en-IN')} kg</div>
                    </div>
                    <div>
                      <div style={{ color: '#5C6B61' }}>Base Rate</div>
                      <div className="font-bold tabular-nums" style={{ color: '#1F1F1F' }} data-testid="freight-override-master-base-rate">₹{fmtMoney(masterForDealer.base_rate)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5C6B61' }}>By Dealer @ Base</div>
                      <div className="font-bold tabular-nums" style={{ color: '#1F1F1F' }} data-testid="freight-override-master-by-dealer">₹{fmtMoney(masterForDealer.by_dealer_at_base)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5C6B61' }}>By Us @ Base</div>
                      <div className="font-bold tabular-nums" style={{ color: '#1F1F1F' }} data-testid="freight-override-master-by-us">₹{fmtMoney(Math.max(0, (masterForDealer.base_rate || 0) - (masterForDealer.by_dealer_at_base || 0)))}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: '#C05C35' }}>
                    No master rate configured for <b>{defaultLocation}</b>. Add one on the Master tab first — otherwise the override has no base to scale from.
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium">Override — By Dealer @ Base (₹)</label>
              <Input type="number" value={form.by_dealer_at_base} onChange={(e) => setForm({ ...form, by_dealer_at_base: e.target.value })} data-testid="freight-override-by-dealer" />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>
                Replaces the dealer share for this dealer at their default location. Scales with weight just like the master value.
                {masterForDealer && form.by_dealer_at_base !== '' && (
                  <> · Δ vs master: <b>₹{fmtMoney((parseFloat(form.by_dealer_at_base) || 0) - (masterForDealer.by_dealer_at_base || 0))}</b></>
                )}
              </p>
            </div>
            {/* Toll + Misc overrides — both optional. Empty ⇒ fall back to the
                master row's default_toll / default_misc. When entered the value
                is used as-is on the freight line, and (per line total math)
                gets ADDED to the base rate to compute the final freight. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Toll (₹) — optional</label>
                <Input
                  type="number"
                  value={form.toll}
                  onChange={(e) => setForm({ ...form, toll: e.target.value })}
                  placeholder={masterForDealer ? `Master: ₹${fmtMoney(masterForDealer.default_toll || 0)}` : 'Master: ₹0.00'}
                  data-testid="freight-override-toll"
                />
                <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Leave blank to use the master&apos;s default toll. When entered, this amount is <b>borne by us</b> (not the dealer).</p>
              </div>
              <div>
                <label className="text-xs font-medium">Misc (₹) — optional</label>
                <Input
                  type="number"
                  value={form.misc}
                  onChange={(e) => setForm({ ...form, misc: e.target.value })}
                  placeholder={masterForDealer ? `Master: ₹${fmtMoney(masterForDealer.default_misc || 0)}` : 'Master: ₹0.00'}
                  data-testid="freight-override-misc"
                />
                <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Leave blank to use the master&apos;s default misc. When entered, this amount is <b>borne by us</b> (not the dealer).</p>
              </div>
            </div>
            {(form.toll !== '' || form.misc !== '') && (
              <div className="text-[11px] rounded p-2" style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                Effective base + add-ons for this dealer:
                <b> ₹{fmtMoney((masterForDealer?.base_rate || 0) + (form.toll === '' ? (masterForDealer?.default_toll || 0) : (parseFloat(form.toll) || 0)) + (form.misc === '' ? (masterForDealer?.default_misc || 0) : (parseFloat(form.misc) || 0)))}</b>
                <span> (base ₹{fmtMoney(masterForDealer?.base_rate || 0)} + toll ₹{fmtMoney(form.toll === '' ? (masterForDealer?.default_toll || 0) : (parseFloat(form.toll) || 0))} + misc ₹{fmtMoney(form.misc === '' ? (masterForDealer?.default_misc || 0) : (parseFloat(form.misc) || 0))})</span>
                {(form.toll !== '' || form.misc !== '') && (
                  <div className="mt-1" style={{ color: '#2B5339' }}>
                    Override toll + misc entered here ({fmtMoney((form.toll === '' ? 0 : (parseFloat(form.toll) || 0)) + (form.misc === '' ? 0 : (parseFloat(form.misc) || 0)))}) will be <b>charged to us</b>, not to the dealer.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} style={{ background: '#2B5339' }} data-testid="freight-override-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================ LINES VIEW ============================
const LinesView = ({ transporters }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Default the Lines date filter to yesterday (both from & to) so the operator
  // lands on the previous business day's freight rows — that's the day they
  // typically reconcile against dispatch invoices. Computed with local-time
  // math so the ISO date matches IST.
  const yesterdayISO = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();
  const [filters, setFilters] = useState({ transporter_ids: [], billed: 'no', date_from: yesterdayISO, date_to: yesterdayISO });
  // Optional alphabetical sort on the Transporter column. null = default order
  // (server order, i.e. newest first). Cycles: null → asc → desc → null.
  const [transporterSort, setTransporterSort] = useState(null);
  const cycleTransporterSort = () => {
    setTransporterSort((p) => (p === null ? 'asc' : p === 'asc' ? 'desc' : null));
  };
  const [editing, setEditing] = useState(null);

  // Monotonic counter — drops stale concurrent fetches (race guard).
  const loadSeqRef = React.useRef(0);
  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const params = {};
      if (filters.transporter_ids && filters.transporter_ids.length) params.transporter_ids = filters.transporter_ids.join(',');
      if (filters.billed) params.billed = filters.billed;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await freightAPI.listLines(params);
      if (seq !== loadSeqRef.current) return;
      setRows(r.data);
    } catch (e) {
      if (seq === loadSeqRef.current) toast.error(e.response?.data?.detail || 'Failed to load freight lines');
    }
    finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.transporter_ids.join(','), filters.billed, filters.date_from, filters.date_to]);

  const resync = async () => {
    setLoading(true);
    try {
      const r = await freightAPI.resyncAll();
      const synced = r.data.synced_lines || 0;
      const orphans = r.data.orphans_removed || 0;
      toast.success(orphans > 0
        ? `Resynced ${synced} line(s), removed ${orphans} orphan(s)`
        : `Resynced ${synced} freight line(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resync failed'); }
    finally { setLoading(false); }
  };

  // Download the currently-filtered Lines list as Excel or PDF. Server
  // reads the exact same filters as the visible table (transporter_id /
  // billed / date_from / date_to) so the export mirrors the UI 1:1.
  const downloadLinesExport = async (kind) => {
    try {
      const params = {};
      if (filters.transporter_ids && filters.transporter_ids.length) params.transporter_ids = filters.transporter_ids.join(',');
      if (filters.billed) params.billed = filters.billed;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = kind === 'excel'
        ? await freightAPI.exportLinesExcel(params)
        : await freightAPI.exportLinesPdf(params);
      const blob = new Blob([res.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `freight_lines.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error('Download failed'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const body = {
      location: editing.location,
      base_rate: parseFloat(editing.base_rate) || 0,
      toll: parseFloat(editing.toll) || 0,
      misc: parseFloat(editing.misc) || 0,
      by_dealer: parseFloat(editing.by_dealer) || 0,
      by_us: parseFloat(editing.by_us) || 0,
      notes: editing.notes || '',
    };
    try {
      await freightAPI.updateLine(editing.id, body);
      toast.success('Line updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Apply optional transporter sort, then window the list (same 30-row + lazy
  // load behaviour as Orders). Totals below still sum over the FULL list.
  const sortedRows = transporterSort
    ? [...rows].sort((a, b) => {
        const av = (a.transporter_name || '').toLowerCase();
        const bv = (b.transporter_name || '').toLowerCase();
        if (av === bv) return 0;
        return transporterSort === 'asc' ? (av < bv ? -1 : 1) : (av < bv ? 1 : -1);
      })
    : rows;
  const { visible: visibleLines, hasMore: linesHasMore, remaining: linesRemaining, loadMoreRef: linesLoadMoreRef, showMore: linesShowMore } =
    useWindowedList(sortedRows, { resetDeps: [filters.transporter_ids.join(','), filters.billed, filters.date_from, filters.date_to, transporterSort, rows.length] });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {(() => {
          const selectedIds = filters.transporter_ids || [];
          // Alphabetised so the dropdown matches how transporters are shown
          // elsewhere (Dealer Splits also sorts alphabetically).
          const sortedTransporters = [...transporters].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          const selectedNames = sortedTransporters
            .filter(t => selectedIds.includes(t.id))
            .map(t => t.name);
          const label = selectedIds.length === 0
            ? 'All transporters'
            : selectedIds.length === 1
              ? (selectedNames[0] || '1 selected')
              : `${selectedIds.length} selected`;
          const toggle = (id, checked) => {
            setFilters((f) => {
              const set = new Set(f.transporter_ids || []);
              if (checked) set.add(id); else set.delete(id);
              return { ...f, transporter_ids: Array.from(set) };
            });
          };
          const clearAll = () => setFilters((f) => ({ ...f, transporter_ids: [] }));
          const selectAll = () => setFilters((f) => ({ ...f, transporter_ids: sortedTransporters.map(t => t.id) }));
          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 justify-between min-w-[190px] bg-white font-normal"
                  data-testid="freight-line-transporter-filter"
                  title={selectedNames.length > 0 ? selectedNames.join(', ') : 'Filter by transporter (multi-select)'}
                >
                  <span className="truncate">{label}</span>
                  <span style={{ color: '#5C6B61', marginLeft: 6 }}>▾</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-0" data-testid="freight-line-transporter-popover">
                <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
                  <span className="text-xs font-medium" style={{ color: '#5C6B61' }}>
                    {selectedIds.length} of {sortedTransporters.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={selectAll} className="text-[11px] underline" style={{ color: '#2B5339' }} data-testid="freight-line-transporter-select-all">All</button>
                    <button type="button" onClick={clearAll} className="text-[11px] underline" style={{ color: '#C05C35' }} data-testid="freight-line-transporter-clear">Clear</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                  {sortedTransporters.length === 0 ? (
                    <div className="text-xs px-3 py-4 text-center" style={{ color: '#5C6B61' }}>No transporters</div>
                  ) : sortedTransporters.map((t) => {
                    const checked = selectedIds.includes(t.id);
                    return (
                      <label
                        key={t.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-[#FBF9F6]"
                        data-testid={`freight-line-transporter-option-${t.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => toggle(t.id, !!v)}
                        />
                        <span className="text-sm">{t.name}</span>
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          );
        })()}
        <select value={filters.billed} onChange={(e) => setFilters({ ...filters, billed: e.target.value })} className="h-9 px-2 border rounded text-sm bg-white" data-testid="freight-line-billed-filter">
          <option value="">All</option>
          <option value="no">Un-billed only</option>
          <option value="yes">Billed only</option>
        </select>
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-9 w-36" data-testid="freight-line-from" />
        <span style={{ color: '#5C6B61' }}>→</span>
        <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-9 w-36" data-testid="freight-line-to" />
        <Button size="sm" variant="outline" onClick={resync} disabled={loading} data-testid="freight-line-resync"><RefreshCw className="w-3 h-3 mr-1" />Resync</Button>
        <Button size="sm" variant="outline" onClick={() => downloadLinesExport('excel')} disabled={loading || rows.length === 0} data-testid="freight-line-excel"><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
        <Button size="sm" variant="outline" onClick={() => downloadLinesExport('pdf')} disabled={loading || rows.length === 0} data-testid="freight-line-pdf"><FileText className="w-3 h-3 mr-1" />PDF</Button>
        <span className="text-sm" style={{ color: '#5C6B61' }}>{rows.length} line{rows.length === 1 ? '' : 's'}</span>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]">
          <thead style={{ background: '#FBF9F6' }}>
            <tr>
              {['Date', 'Order', 'Dealer', 'Transporter', 'Vehicle', 'Location', 'Wt (kg)', 'Base', 'Toll', 'Misc', 'Total', 'By Dealer', 'By Us', 'Status', ''].map((h) => {
                if (h === 'Transporter') {
                  return (
                    <th
                      key={h}
                      className="p-2 text-left font-medium whitespace-nowrap cursor-pointer select-none hover:bg-white"
                      style={{ color: '#5C6B61' }}
                      onClick={cycleTransporterSort}
                      data-testid="freight-line-sort-transporter"
                      title="Click to sort by Transporter (A→Z → Z→A → default)"
                    >
                      Transporter{transporterSort === 'asc' ? ' ▲' : transporterSort === 'desc' ? ' ▼' : ' ⇅'}
                    </th>
                  );
                }
                return (
                  <th key={h} className="p-2 text-left font-medium whitespace-nowrap" style={{ color: '#5C6B61' }}>{h}</th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="p-6 text-center" style={{ color: '#5C6B61' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={15} className="p-6 text-center" style={{ color: '#5C6B61' }}>No freight lines match the current filters.</td></tr>
            ) : (<>{visibleLines.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: '#E2DFD8' }} data-testid="freight-line-row">
                <td className="p-2 whitespace-nowrap">{fmtDate(r.dispatched_at)}</td>
                <td className="p-2">#{r.order_short}</td>
                <td className="p-2">{r.dealer_name}</td>
                <td className="p-2">{r.transporter_name}</td>
                <td className="p-2">{r.vehicle_number}</td>
                <td className="p-2">{r.location || <span style={{ color: '#C05C35' }}>—</span>}</td>
                <td className="p-2 tabular-nums">{Math.round(r.weight_kg_total || 0)}</td>
                <td className="p-2 tabular-nums">{fmtMoney(r.base_rate)}</td>
                <td className="p-2 tabular-nums">{fmtMoney(r.toll)}</td>
                <td className="p-2 tabular-nums">{fmtMoney(r.misc)}</td>
                <td className="p-2 tabular-nums font-semibold">{fmtMoney(r.total_freight)}</td>
                <td className="p-2 tabular-nums">{fmtMoney(r.by_dealer)}</td>
                <td className="p-2 tabular-nums">{fmtMoney(r.by_us)}</td>
                <td className="p-2">
                  {r.bill_id ? (
                    <Badge variant="outline" style={{ borderColor: '#2B5339', color: '#2B5339' }}>Bill #{r.bill_no}</Badge>
                  ) : r.auto_computed ? (
                    <Badge variant="outline" style={{ borderColor: '#5C6B61', color: '#5C6B61' }}>Auto</Badge>
                  ) : (
                    <Badge variant="outline" style={{ borderColor: '#C05C35', color: '#C05C35' }}>Manual</Badge>
                  )}
                </td>
                <td className="p-2">
                  {!r.bill_id && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...r })} className="h-7" data-testid="freight-line-edit" title="Edit this line manually"><Edit className="w-3 h-3" /></Button>
                      {!r.auto_computed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await freightAPI.recomputeLine(r.id);
                              toast.success('Recomputed from master');
                              load();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                          }}
                          className="h-7"
                          style={{ borderColor: '#2B5339', color: '#2B5339' }}
                          data-testid="freight-line-recompute"
                          title="Re-apply master rate to this Manual line (overrides the manual flag)"
                        ><RefreshCw className="w-3 h-3" /></Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
              {linesHasMore && (
                <tr ref={linesLoadMoreRef} data-testid="freight-lines-load-more">
                  <td colSpan={15} className="p-3 text-center" style={{ background: '#FBF9F6' }}>
                    <Button variant="outline" size="sm" onClick={linesShowMore} data-testid="freight-lines-show-more-btn">
                      Show more ({linesRemaining} more)
                    </Button>
                  </td>
                </tr>
              )}
              {/* Totals row — sums Total, By Dealer, By Us over ALL filtered lines (not just the windowed slice) */}
              {(() => {
                const totals = rows.reduce((s, r) => ({
                  total: s.total + Number(r.total_freight || 0),
                  by_dealer: s.by_dealer + Number(r.by_dealer || 0),
                  by_us: s.by_us + Number(r.by_us || 0),
                }), { total: 0, by_dealer: 0, by_us: 0 });
                return (
                  <tr className="border-t font-bold" style={{ borderColor: '#2B5339', background: '#FBF9F6' }} data-testid="freight-line-totals">
                    <td className="p-2" colSpan={10} style={{ color: '#2B5339' }}>TOTAL</td>
                    <td className="p-2 tabular-nums" style={{ color: '#2B5339' }}>{fmtMoney(totals.total)}</td>
                    <td className="p-2 tabular-nums" style={{ color: '#2B5339' }}>{fmtMoney(totals.by_dealer)}</td>
                    <td className="p-2 tabular-nums" style={{ color: '#2B5339' }}>{fmtMoney(totals.by_us)}</td>
                    <td className="p-2" colSpan={2}></td>
                  </tr>
                );
              })()}
            </>)}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Freight Line</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-xs" style={{ color: '#5C6B61' }}>
                Order <b>#{editing.order_short}</b> · {editing.dealer_name} · {editing.transporter_name} · {Math.round(editing.weight_kg_total || 0)} kg
              </div>
              <div>
                <label className="text-xs font-medium">Location</label>
                <Input value={editing.location || ''} onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="freight-line-loc" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium">Base Rate</label>
                  <Input type="number" value={editing.base_rate} onChange={(e) => {
                    const next = { ...editing, base_rate: e.target.value };
                    const tot = (parseFloat(e.target.value) || 0) + (parseFloat(next.toll) || 0) + (parseFloat(next.misc) || 0);
                    next.by_us = Math.max(0, tot - (parseFloat(next.by_dealer) || 0));
                    setEditing(next);
                  }} data-testid="freight-line-base" />
                </div>
                <div>
                  <label className="text-xs font-medium">Toll</label>
                  <Input type="number" value={editing.toll} onChange={(e) => {
                    const next = { ...editing, toll: e.target.value };
                    const tot = (parseFloat(next.base_rate) || 0) + (parseFloat(e.target.value) || 0) + (parseFloat(next.misc) || 0);
                    next.by_us = Math.max(0, tot - (parseFloat(next.by_dealer) || 0));
                    setEditing(next);
                  }} data-testid="freight-line-toll" />
                </div>
                <div>
                  <label className="text-xs font-medium">Misc</label>
                  <Input type="number" value={editing.misc} onChange={(e) => {
                    const next = { ...editing, misc: e.target.value };
                    const tot = (parseFloat(next.base_rate) || 0) + (parseFloat(next.toll) || 0) + (parseFloat(e.target.value) || 0);
                    next.by_us = Math.max(0, tot - (parseFloat(next.by_dealer) || 0));
                    setEditing(next);
                  }} data-testid="freight-line-misc" />
                </div>
              </div>
              <div className="rounded p-2 text-sm tabular-nums" style={{ background: '#FBF9F6' }}>
                Total freight: <b>₹{fmtMoney((parseFloat(editing.base_rate) || 0) + (parseFloat(editing.toll) || 0) + (parseFloat(editing.misc) || 0))}</b>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium">By Dealer</label>
                  <Input type="number" value={editing.by_dealer} onChange={(e) => {
                    const tot = (parseFloat(editing.base_rate) || 0) + (parseFloat(editing.toll) || 0) + (parseFloat(editing.misc) || 0);
                    setEditing({ ...editing, by_dealer: e.target.value, by_us: Math.max(0, tot - (parseFloat(e.target.value) || 0)) });
                  }} data-testid="freight-line-by-dealer" />
                </div>
                <div>
                  <label className="text-xs font-medium">By Us</label>
                  <Input type="number" value={editing.by_us} onChange={(e) => {
                    const tot = (parseFloat(editing.base_rate) || 0) + (parseFloat(editing.toll) || 0) + (parseFloat(editing.misc) || 0);
                    setEditing({ ...editing, by_us: e.target.value, by_dealer: Math.max(0, tot - (parseFloat(e.target.value) || 0)) });
                  }} data-testid="freight-line-by-us" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium">Notes</label>
                <Input value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} style={{ background: '#2B5339' }} data-testid="freight-line-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================ BILLS VIEW ============================
const BillsView = ({ transporters }) => {
  const confirm = useConfirm();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ transporter_ids: [], period_from: '', period_to: '' });
  const [eligibleLines, setEligibleLines] = useState([]);
  const [picked, setPicked] = useState({});
  // Status filter for bills list.
  // 'all' = pending + partial (paid EXPLICITLY excluded per operator spec).
  // 'pending' / 'partial' / 'paid' = exact match.
  const [statusFilter, setStatusFilter] = useState('all');
  const filteredBills = React.useMemo(() => {
    const norm = (b) => (b.status || 'pending').toLowerCase();
    if (statusFilter === 'all') return bills.filter(b => norm(b) !== 'paid');
    return bills.filter(b => norm(b) === statusFilter);
  }, [bills, statusFilter]);
  const { visible: visibleBills, hasMore: billsHasMore, remaining: billsRemaining, loadMoreRef: billsLoadMoreRef, showMore: billsShowMore } =
    useWindowedList(filteredBills, { resetDeps: [statusFilter, filteredBills.length] });

  const loadBills = async () => {
    setLoading(true);
    try { const r = await freightAPI.listBills(); setBills(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load bills'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadBills(); }, []);

  const openGenerate = async () => {
    setGen({ transporter_ids: [], period_from: '', period_to: '' });
    setEligibleLines([]);
    setPicked({});
    setGenOpen(true);
  };

  const loadEligible = async () => {
    if (!gen.transporter_ids || gen.transporter_ids.length === 0) { toast.error('Pick at least one transporter'); return; }
    try {
      const r = await freightAPI.listLines({
        transporter_ids: gen.transporter_ids.join(','),
        billed: 'no',
        date_from: gen.period_from || undefined,
        date_to: gen.period_to || undefined,
      });
      setEligibleLines(r.data);
      const pick = {};
      r.data.forEach((ln) => { pick[ln.id] = true; });
      setPicked(pick);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const finalizeBill = async () => {
    const line_ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (!line_ids.length) { toast.error('Pick at least one line'); return; }
    if (!gen.period_from || !gen.period_to) { toast.error('Period dates are required'); return; }
    // Group picked lines by transporter_id — backend requires one bill per
    // transporter, so we generate a separate bill for each group.
    const linesById = new Map(eligibleLines.map(l => [l.id, l]));
    const grouped = new Map();
    for (const lid of line_ids) {
      const ln = linesById.get(lid);
      if (!ln) continue;
      const tid = ln.transporter_id;
      if (!grouped.has(tid)) grouped.set(tid, []);
      grouped.get(tid).push(lid);
    }
    if (grouped.size === 0) { toast.error('Could not resolve transporter for selected lines'); return; }
    const results = [];
    const failures = [];
    for (const [tid, ids] of grouped.entries()) {
      try {
        const r = await freightAPI.createBill({
          transporter_id: tid,
          period_from: gen.period_from,
          period_to: gen.period_to,
          line_ids: ids,
        });
        results.push(r.data.bill_no);
      } catch (e) {
        const tname = transporters.find(x => x.id === tid)?.name || tid;
        failures.push(`${tname}: ${e.response?.data?.detail || 'failed'}`);
      }
    }
    if (results.length > 0) {
      toast.success(results.length === 1
        ? `Bill #${results[0]} created`
        : `${results.length} bills created (#${results.join(', #')})`);
    }
    if (failures.length > 0) toast.error(failures.join(' · '));
    if (results.length > 0) {
      setGenOpen(false);
      loadBills();
    }
  };

  const downloadBill = async (bill, kind) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/freight/bills/${bill.id}/${kind}`;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `freight_bill_${bill.bill_no}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error('Download failed'); }
  };

  const setStatus = async (bill, status) => {
    try {
      await freightAPI.updateBillStatus(bill.id, { status });
      toast.success(`Status: ${status.toUpperCase()}`);
      loadBills();
      if (selectedBill?.id === bill.id) {
        const fresh = await freightAPI.getBill(bill.id);
        setSelectedBill(fresh.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeBill = async (bill) => {
    if (!(await confirm({
      title: `Delete Bill #${bill.bill_no}?`,
      description: 'All lines on this bill will be un-billed and become editable again. The bill record is removed.',
      confirmLabel: 'Delete bill',
      destructive: true,
    }))) return;
    try { await freightAPI.deleteBill(bill.id); toast.success('Bill deleted'); setSelectedBill(null); loadBills(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openBill = async (bill) => {
    try { const r = await freightAPI.getBill(bill.id); setSelectedBill(r.data); }
    catch (e) { toast.error('Failed to load bill'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: '#5C6B61' }}>Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-2 border rounded text-sm bg-white"
            style={{ borderColor: '#2B5339', color: '#2B5339' }}
            data-testid="freight-bill-status-filter"
          >
            <option value="all">All (Pending + Partial)</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
          <p className="text-sm" style={{ color: '#5C6B61' }}>{filteredBills.length} bill{filteredBills.length === 1 ? '' : 's'} {statusFilter !== 'all' ? `(${statusFilter})` : 'shown'} · {bills.length} total on file</p>
        </div>
        <Button onClick={openGenerate} size="sm" style={{ background: '#2B5339' }} data-testid="freight-bill-generate">
          <Receipt className="w-4 h-4 mr-1" /> Generate Bill
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: '#FBF9F6' }}>
            <tr>
              {['Bill #', 'Transporter', 'Period', 'Lines', 'Total', 'By Dealer', 'By Us', 'Paid', 'Status', ''].map((h) => (
                <th key={h} className="p-2 text-left font-medium" style={{ color: '#5C6B61' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="p-6 text-center" style={{ color: '#5C6B61' }}>Loading…</td></tr>
            ) : filteredBills.length === 0 ? (
              <tr><td colSpan={10} className="p-6 text-center" style={{ color: '#5C6B61' }}>{bills.length === 0 ? 'No bills yet.' : `No ${statusFilter === 'all' ? 'unpaid' : statusFilter} bills.`}</td></tr>
            ) : visibleBills.map((b) => (
              <tr key={b.id} className="border-t cursor-pointer hover:bg-[#FBF9F6]" style={{ borderColor: '#E2DFD8' }} onClick={() => openBill(b)} data-testid="freight-bill-row">
                <td className="p-2 font-bold">#{b.bill_no}</td>
                <td className="p-2">{b.transporter_name}</td>
                <td className="p-2 whitespace-nowrap">{fmtDate(b.period_from)} – {fmtDate(b.period_to)}</td>
                <td className="p-2">{(b.line_ids || []).length}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(b.total_freight)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(b.by_dealer_total)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(b.by_us_total)}</td>
                <td className="p-2 tabular-nums">₹{fmtMoney(b.paid_amount)}</td>
                <td className="p-2">
                  <Badge variant="outline" style={{
                    borderColor: b.status === 'paid' ? '#2B5339' : b.status === 'partial' ? '#E89D4F' : '#C05C35',
                    color: b.status === 'paid' ? '#2B5339' : b.status === 'partial' ? '#E89D4F' : '#C05C35',
                  }}>{(b.status || 'pending').toUpperCase()}</Badge>
                </td>
                <td className="p-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => downloadBill(b, 'pdf')} className="h-7 mr-1" data-testid="freight-bill-pdf"><FileText className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => downloadBill(b, 'excel')} className="h-7 mr-1" data-testid="freight-bill-excel"><FileSpreadsheet className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => removeBill(b)} className="h-7" style={{ borderColor: '#C05C35', color: '#C05C35' }} data-testid="freight-bill-delete"><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
            {billsHasMore && (
              <tr ref={billsLoadMoreRef} data-testid="freight-bills-load-more">
                <td colSpan={10} className="p-3 text-center" style={{ background: '#FBF9F6' }}>
                  <Button variant="outline" size="sm" onClick={billsShowMore} data-testid="freight-bills-show-more-btn">
                    Show more ({billsRemaining} more)
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Bill detail panel */}
      <Dialog open={!!selectedBill} onOpenChange={(o) => { if (!o) setSelectedBill(null); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Bill #{selectedBill?.bill_no} · {selectedBill?.transporter_name}</DialogTitle>
          </DialogHeader>
          {selectedBill && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center text-sm">
                <span style={{ color: '#5C6B61' }}>Period: <b>{fmtDate(selectedBill.period_from)} – {fmtDate(selectedBill.period_to)}</b></span>
                <span style={{ color: '#5C6B61' }}>· Lines: <b>{(selectedBill.lines || []).length}</b></span>
                <span style={{ color: '#5C6B61' }}>· Total: <b>₹{fmtMoney(selectedBill.total_freight)}</b></span>
                <div className="ml-auto flex gap-1">
                  {['pending', 'partial', 'paid'].map((s) => (
                    <Button key={s} size="sm" variant="outline" onClick={() => setStatus(selectedBill, s)} style={{
                      background: selectedBill.status === s ? '#2B5339' : 'white',
                      color: selectedBill.status === s ? 'white' : '#2B5339',
                      borderColor: '#2B5339',
                    }} data-testid={`freight-bill-status-${s}`}>{s.toUpperCase()}</Button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto rounded border" style={{ borderColor: '#E2DFD8' }}>
                <table className="w-full text-xs">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>{['#', 'Date', 'Order', 'Dealer', 'Vehicle', 'Location', 'Wt', 'Base', 'Toll', 'Misc', 'Total', 'By Dealer', 'By Us'].map(h => <th key={h} className="p-1.5 text-left font-medium" style={{ color: '#5C6B61' }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(selectedBill.lines || []).map((ln, i) => (
                      <tr key={ln.id} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                        <td className="p-1.5">{i + 1}</td>
                        <td className="p-1.5 whitespace-nowrap">{fmtDate(ln.dispatched_at)}</td>
                        <td className="p-1.5">#{ln.order_short}</td>
                        <td className="p-1.5">{ln.dealer_name}</td>
                        <td className="p-1.5">{ln.vehicle_number}</td>
                        <td className="p-1.5">{ln.location}</td>
                        <td className="p-1.5 tabular-nums">{Math.round(ln.weight_kg_total || 0)}</td>
                        <td className="p-1.5 tabular-nums">{fmtMoney(ln.base_rate)}</td>
                        <td className="p-1.5 tabular-nums">{fmtMoney(ln.toll)}</td>
                        <td className="p-1.5 tabular-nums">{fmtMoney(ln.misc)}</td>
                        <td className="p-1.5 tabular-nums font-semibold">{fmtMoney(ln.total_freight)}</td>
                        <td className="p-1.5 tabular-nums">{fmtMoney(ln.by_dealer)}</td>
                        <td className="p-1.5 tabular-nums">{fmtMoney(ln.by_us)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadBill(selectedBill, 'pdf')}><FileText className="w-3 h-3 mr-1" />PDF</Button>
                <Button size="sm" variant="outline" onClick={() => downloadBill(selectedBill, 'excel')}><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate-bill dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Generate Freight Bill</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="col-span-3">
              <label className="text-xs font-medium">Transporter(s) *</label>
              {(() => {
                const selectedIds = gen.transporter_ids || [];
                const sortedTransporters = [...transporters].sort((a, b) =>
                  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                const selectedNames = sortedTransporters.filter(t => selectedIds.includes(t.id)).map(t => t.name);
                const label = selectedIds.length === 0
                  ? '-- pick transporter(s) --'
                  : selectedIds.length === 1
                    ? (selectedNames[0] || '1 selected')
                    : `${selectedIds.length} selected`;
                const toggle = (id, checked) => {
                  setGen((g) => {
                    const set = new Set(g.transporter_ids || []);
                    if (checked) set.add(id); else set.delete(id);
                    return { ...g, transporter_ids: Array.from(set) };
                  });
                };
                const selectAll = () => setGen((g) => ({ ...g, transporter_ids: sortedTransporters.map(t => t.id) }));
                const clearAll = () => setGen((g) => ({ ...g, transporter_ids: [] }));
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-9 justify-between bg-white font-normal"
                        data-testid="freight-bill-transporter"
                        title={selectedNames.length > 0 ? selectedNames.join(', ') : 'Pick transporter(s) — multi-select'}
                      >
                        <span className="truncate">{label}</span>
                        <span style={{ color: '#5C6B61', marginLeft: 6 }}>▾</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-0" data-testid="freight-bill-transporter-popover">
                      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
                        <span className="text-xs font-medium" style={{ color: '#5C6B61' }}>
                          {selectedIds.length} of {sortedTransporters.length} selected
                        </span>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={selectAll} className="text-[11px] underline" style={{ color: '#2B5339' }} data-testid="freight-bill-transporter-select-all">All</button>
                          <button type="button" onClick={clearAll} className="text-[11px] underline" style={{ color: '#C05C35' }} data-testid="freight-bill-transporter-clear">Clear</button>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-1">
                        {sortedTransporters.length === 0 ? (
                          <div className="text-xs px-3 py-4 text-center" style={{ color: '#5C6B61' }}>No transporters</div>
                        ) : sortedTransporters.map((t) => {
                          const checked = selectedIds.includes(t.id);
                          return (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-[#FBF9F6]"
                              data-testid={`freight-bill-transporter-option-${t.id}`}
                            >
                              <Checkbox checked={checked} onCheckedChange={(v) => toggle(t.id, !!v)} />
                              <span className="text-sm">{t.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })()}
              {gen.transporter_ids.length > 1 && (
                <div className="text-[11px] mt-1" style={{ color: '#5C6B61' }}>
                  Multiple transporters selected — one separate bill will be created per transporter for the picked lines.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium">From *</label>
              <Input type="date" value={gen.period_from} onChange={(e) => setGen({ ...gen, period_from: e.target.value })} data-testid="freight-bill-from" />
            </div>
            <div>
              <label className="text-xs font-medium">To *</label>
              <Input type="date" value={gen.period_to} onChange={(e) => setGen({ ...gen, period_to: e.target.value })} data-testid="freight-bill-to" />
            </div>
            <div className="flex items-end">
              <Button onClick={loadEligible} className="h-9" variant="outline" data-testid="freight-bill-load-lines">Load lines</Button>
            </div>
          </div>
          {eligibleLines.length > 0 && (
            <div className="overflow-x-auto rounded border" style={{ borderColor: '#E2DFD8', maxHeight: 320 }}>
              <table className="w-full text-xs">
                <thead className="sticky top-0" style={{ background: '#FBF9F6' }}>
                  <tr>
                    <th className="p-1.5">
                      <input type="checkbox"
                        checked={eligibleLines.every(l => picked[l.id])}
                        onChange={(e) => { const p = {}; eligibleLines.forEach(l => { p[l.id] = e.target.checked; }); setPicked(p); }}
                        data-testid="freight-bill-pick-all" />
                    </th>
                    {['Date', 'Order', 'Transporter', 'Dealer', 'Vehicle', 'Wt', 'Total', 'By Us'].map(h => <th key={h} className="p-1.5 text-left font-medium" style={{ color: '#5C6B61' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {eligibleLines.map((ln) => (
                    <tr key={ln.id} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                      <td className="p-1.5"><input type="checkbox" checked={!!picked[ln.id]} onChange={(e) => setPicked({ ...picked, [ln.id]: e.target.checked })} data-testid="freight-bill-pick" /></td>
                      <td className="p-1.5 whitespace-nowrap">{fmtDate(ln.dispatched_at)}</td>
                      <td className="p-1.5">#{ln.order_short}</td>
                      <td className="p-1.5">{ln.transporter_name}</td>
                      <td className="p-1.5">{ln.dealer_name}</td>
                      <td className="p-1.5">{ln.vehicle_number}</td>
                      <td className="p-1.5">{Math.round(ln.weight_kg_total || 0)}</td>
                      <td className="p-1.5 tabular-nums font-semibold">{fmtMoney(ln.total_freight)}</td>
                      <td className="p-1.5 tabular-nums">{fmtMoney(ln.by_us)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={finalizeBill} style={{ background: '#2B5339' }} data-testid="freight-bill-finalize">Finalize bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
